import type { WeldTelemetrySampleRow } from './weldTelemetryClient'
import { convertWeldDataValue, getParameterUnitSuffix } from './weldUnitUtils'

/**
 * Per-column filter state. `null` = "all values selected" (no active filter).
 * An empty array = "nothing matches" (active filter that hides every row).
 */
export interface ColumnFilterState {
  /** Selected option keys (raw string form). `null` means no filter active. */
  values: string[] | null
  /** Free-text contains/equals filter from the top of the popover. */
  text: string
}

export const EMPTY_FILTER: ColumnFilterState = { values: null, text: '' }

export type ColumnFiltersMap = Record<string, ColumnFilterState>

/**
 * Format a numeric value the same way the table cell renders it. Mirrors
 * `fmtNumber` in PassAnalyticsTable.tsx so the filter checklist labels and
 * the cell values are byte-identical (otherwise selecting "13.40" wouldn't
 * match a row whose cell shows "13.40").
 */
function fmtNumberLabel(value: number | null | undefined, digits: number): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return '—'
  const rounded = Math.abs(value - Math.round(value)) < 1e-9
  return rounded ? String(Math.round(value)) : Number(value).toFixed(digits)
}

/**
 * Format a recordedAt ISO into the same `DD-MMM-YYYY, h:mm:ss A` shape used
 * by the Time column. Kept here (instead of imported from PassAnalyticsTable)
 * so the filter utility doesn't depend on the React component.
 */
const TIME_FMT_PARTS = new Intl.DateTimeFormat('en-GB', {
  timeZone: 'UTC',
  day: '2-digit',
  month: 'short',
  year: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
  second: '2-digit',
  hour12: true
})

const SHORT_MONTHS = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
] as const

export function formatRecordedAt(iso: string | null): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  const parts = TIME_FMT_PARTS.formatToParts(d)
  const get = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((p) => p.type === type)?.value ?? ''
  const day = get('day')
  const month = SHORT_MONTHS[d.getUTCMonth()]
  const year = get('year')
  const hour = get('hour')
  const minute = get('minute')
  const second = get('second')
  const dayPeriod = get('dayPeriod').toUpperCase()
  return `${day}-${month}-${year}, ${hour}:${minute}:${second} ${dayPeriod}`
}

/**
 * Read the unit-converted display value for one column on one sample.
 * Returns the formatted string (matching the cell), or `null` if the
 * column has no value to filter on (we'll bucket those under '—' so the
 * user can include/exclude empty rows).
 */
const COLUMN_DIGITS: Record<string, number> = {
  travelSpeed: 2,
  volts: 2,
  amps: 1,
  wireIpm: 0,
  widthMil: 1,
  targetVlt: 2,
  csBias: 2,
  bpm: 1,
  totWireIn: 2,
  trueEnergy: 2,
  heat: 2,
  tilt: 2,
  distance: 2,
  duration: 3
}

const COLUMN_PARAM_KEYS: Record<string, string> = {
  tilt: 'tilt',
  distance: 'distance',
  travelSpeed: 'travelSpeed',
  volts: 'volts',
  amps: 'amps',
  wireIpm: 'wireIpm',
  widthMil: 'widthMil',
  targetVlt: 'targetVlt',
  csBias: 'csBias',
  bpm: 'bpm',
  totWireIn: 'totWireIn',
  trueEnergy: 'trueEnergy',
  heat: 'rprtHeat',
  duration: 'duration'
}

/**
 * Read a sample's filter-key for one column. Used to build the option list
 * (unique values) AND to test whether a row passes a filter.
 */
export function readFilterKey(sample: WeldTelemetrySampleRow, columnKey: string): string {
  switch (columnKey) {
    case 'sl-no':
      return ''
    case 'event':
      return String(sample.evtNo)
    case 'time':
      return formatRecordedAt(sample.recordedAt)
    case 'pass':
      return ''
    case 'torch':
      return sample.torch
    case 'bugType':
      return sample.bugType ?? '—'
    case 'duration': {
      const v = sample.duration
      return fmtNumberLabel(typeof v === 'number' ? v : null, 3)
    }
    case 'heat': {
      const raw = sample.rprtHeat ?? sample.calcHeat
      const converted = convertWeldDataValue(raw ?? null, 'rprtHeat')
      return fmtNumberLabel(converted, 2)
    }
    default: {
      const paramKey = COLUMN_PARAM_KEYS[columnKey]
      if (!paramKey) return ''
      const raw = (sample as unknown as Record<string, unknown>)[columnKey]
      const num = typeof raw === 'number' ? raw : null
      const converted = convertWeldDataValue(num, paramKey)
      const digits = COLUMN_DIGITS[columnKey] ?? 2
      return fmtNumberLabel(converted, digits)
    }
  }
}

/**
 * Build the sorted unique-value option list for one column from the source
 * samples. Numeric columns are sorted ascending by their numeric value;
 * strings sort alphabetically. The em-dash bucket ('—') is always last.
 */
export function buildFilterOptions(
  samples: WeldTelemetrySampleRow[],
  columnKey: string,
  passLabel: string
): string[] {
  const set = new Set<string>()
  if (columnKey === 'pass') {
    return passLabel ? [passLabel] : []
  }
  // Sl.no has no meaningful filter values (it's just an arbitrary row
  // counter); return an empty option list so callers can hide the funnel.
  if (columnKey === 'sl-no') return []
  for (const s of samples) {
    set.add(readFilterKey(s, columnKey))
  }
  const arr = Array.from(set)
  // Pull '—' (missing value bucket) to the end.
  const dash = arr.filter((v) => v === '—')
  const real = arr.filter((v) => v !== '—')
  // Sort by numeric value when every entry parses as a number; otherwise
  // alphabetically. Empty strings don't appear in the option list (they
  // come from Sl.no / Pass which are skipped above).
  const allNumeric = real.every((v) => v !== '' && !Number.isNaN(parseFloat(v)))
  if (allNumeric) {
    real.sort((a, b) => parseFloat(a) - parseFloat(b))
  } else {
    real.sort((a, b) => a.localeCompare(b))
  }
  return [...real, ...dash]
}

/**
 * Filter samples by the active per-column filter state. A sample passes
 * iff every column with an active filter accepts it. Rows are filtered
 * in O(samples * activeColumns) which is fine for the < 50k samples we
 * see in practice.
 */
export function applyColumnFilters(
  samples: WeldTelemetrySampleRow[],
  filters: ColumnFiltersMap
): WeldTelemetrySampleRow[] {
  const activeEntries = Object.entries(filters).filter(([, f]) => isFilterActive(f))
  if (activeEntries.length === 0) return samples
  return samples.filter((s) =>
    activeEntries.every(([key, filter]) => sampleMatchesFilter(s, key, filter))
  )
}

export function isFilterActive(filter: ColumnFilterState | undefined): boolean {
  if (!filter) return false
  if (filter.values !== null) return true
  if (filter.text.trim() !== '') return true
  return false
}

function sampleMatchesFilter(
  sample: WeldTelemetrySampleRow,
  columnKey: string,
  filter: ColumnFilterState
): boolean {
  const key = readFilterKey(sample, columnKey)
  if (filter.values !== null) {
    if (!filter.values.includes(key)) return false
  }
  const text = filter.text.trim().toLowerCase()
  if (text) {
    if (!key.toLowerCase().includes(text)) return false
  }
  return true
}

export function getColumnUnitLabel(columnKey: string): string | null {
  const paramKey = COLUMN_PARAM_KEYS[columnKey]
  if (!paramKey) return null
  return getParameterUnitSuffix(paramKey) || null
}
